import React from "react";
import type {
  DepEditOp,
  DepGraph,
  DraftPayload,
  PendingNewActivity,
  PendingWorkCodeAdd,
  ZoneTuple,
} from "../../../../models";
import { stageTaktifications } from "../../draft/draftOps";
import type { ActivityInstance, ZoneRow } from "../../draft/types";
import type { InlineAddArgs } from "../../depCore/DepTable/InlineAddRow/InlineAddRow";
import { makeId } from "../../../../utils/id";
import {
  ensureZoneRows,
  foldedZoneKey,
  hashColor,
  type PaletteActivity,
} from "../helpers";

interface Args {
  rows: ZoneRow[];
  setRows: React.Dispatch<React.SetStateAction<ZoneRow[]>>;
  setInstances: React.Dispatch<React.SetStateAction<ActivityInstance[]>>;
  setPendingOps: React.Dispatch<React.SetStateAction<DraftPayload>>;
  pendingNewActivities: PendingNewActivity[];
  paletteByKey: Map<string, PaletteActivity>;
  calendarIndexFor: (iso: string | null) => number;
  depGraphRef: React.MutableRefObject<DepGraph | null>;
  pushUndo: (label?: string) => void;
  /** The dep-graph refetch-hold toggle: true while a row is being edited so the
   *  forward pass is held, false on Done / Cancel when it runs once. */
  onHold: (held: boolean) => void;
}

/** One staged add, rendered in-place with "—" dates until the held pass runs. */
export interface LocalAdd {
  draftId: string;
  name: string;
  code: string;
  codeName: string;
  color: string;
  zone: { a: string; b: string; c: string } | null;
  days: number;
  positionAfterId: string | null;
}

export interface InlineAdd {
  /** Where each inline add was typed (draft id → the activity above it). */
  addPos: Map<string, string | null>;
  /** Staged adds the graph doesn't carry yet, in insertion order. */
  localAdds: LocalAdd[];
  /** Stage a quick-add batch; returns the new activity's draft id. */
  commit: (args: InlineAddArgs) => string;
  /** Clear the in-flight add positions (on draft reset / load). */
  cancel: () => void;
  /** Drive the dep-graph refetch-hold (wired to the row's editing state). */
  onHoldChange: (held: boolean) => void;
}

export function useInlineAdd({
  rows,
  setRows,
  setInstances,
  setPendingOps,
  pendingNewActivities,
  paletteByKey,
  calendarIndexFor,
  depGraphRef,
  pushUndo,
  onHold,
}: Args): InlineAdd {
  // Where each inline add was typed (draft id → the activity above it). Until
  // the held forward pass runs, the row renders right there with "—" dates.
  const [localAddPos, setLocalAddPos] = React.useState<Map<string, string | null>>(
    () => new Map(),
  );

  // Stage a quick-add batch: one level, many activities, typed like a list.
  // Codes that aren't on this section's palette yet (created in the picker, or
  // reused from another section) stage their chips alongside; coded rows get a
  // board bar at the data date (the live re-plan snaps them once the graph
  // returns); "Link in order" chains the rows Finish→Start as laid out, off an
  // optional anchor. Afterwards the first new activity is focused exactly as
  // if it had been clicked.
  const handleInlineAdd = React.useCallback(
    (args: InlineAddArgs): string => {
      pushUndo();
      const g = depGraphRef.current;
      const code = args.code || null;
      const draftId = makeId("new");

      const codeAdd: PendingWorkCodeAdd | null =
        code && !paletteByKey.has(code)
          ? {
              code,
              prefix: args.codeName || code,
              label: args.codeName || code,
              color: args.codeColor || hashColor(code),
              default_duration_days: args.days,
            }
          : null;

      const entry: PendingNewActivity = {
        draft_id: draftId,
        name: args.name,
        takt_a: args.zone?.a || null,
        takt_b: args.zone?.b || null,
        takt_c: args.zone?.c || null,
        work_code: code,
        duration_days: args.days,
      };

      // A graph id that's a draft id stays a string ref; committed → numeric.
      const refFor = (id: string): number | string => {
        if (g?.activities[id]?.is_new) return id;
        const n = Number(id);
        return Number.isFinite(n) ? n : id;
      };
      const edges: DepEditOp[] = [];
      for (const predId of args.predIds) {
        edges.push({
          op: "add",
          pred_activity_id: refFor(predId),
          succ_activity_id: draftId,
          pred_type: "PR_FS",
          lag_hours: 0,
        });
      }
      for (const succId of args.succIds) {
        edges.push({
          op: "add",
          pred_activity_id: draftId,
          succ_activity_id: refFor(succId),
          pred_type: "PR_FS",
          lag_hours: 0,
        });
      }

      if (code && args.zone) {
        const zone: ZoneTuple = {
          takt_a: args.zone.a,
          takt_b: args.zone.b,
          takt_c: args.zone.c,
        };
        const { rows: nextRows, rowIdByZone } = ensureZoneRows(rows, [zone]);
        const rowId = rowIdByZone.get(foldedZoneKey(zone.takt_a, zone.takt_b, zone.takt_c));
        if (rowId) {
          setRows(nextRows);
          setInstances((prev) => [
            ...prev,
            {
              instanceId: makeId("inst"),
              rowId,
              activityKey: code,
              start_index: calendarIndexFor(g?.data_date ?? null),
              duration_days: args.days,
              source_activity_id: null,
              draftId,
            },
          ]);
        }
      }

      setPendingOps((prev) => {
        let next = codeAdd ? stageTaktifications(prev, [], [codeAdd]) : prev;
        return {
          ...next,
          pending_new_activities: [...next.pending_new_activities, entry],
          pending_dep_edits: [...next.pending_dep_edits, ...edges],
        };
      });
      setLocalAddPos((prev) => new Map(prev).set(draftId, args.positionAfterId));
      return draftId;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, calendarIndexFor, pushUndo],
  );

  // Staged adds the graph doesn't carry yet, in insertion order, rendered as
  // in-place rows with "—" dates until the held pass runs.
  const localAdds = React.useMemo<LocalAdd[]>(() => {
    if (localAddPos.size === 0) return [];
    return pendingNewActivities
      .filter((n) => localAddPos.has(n.draft_id))
      .map((n) => {
        const code = n.work_code ?? "";
        const chip = code ? paletteByKey.get(code) : undefined;
        const hasZone = Boolean(n.takt_a || n.takt_b || n.takt_c);
        return {
          draftId: n.draft_id,
          name: n.name,
          code,
          codeName: chip?.name ?? code,
          color: chip?.color ?? "",
          zone: hasZone
            ? { a: n.takt_a ?? "", b: n.takt_b ?? "", c: n.takt_c ?? "" }
            : null,
          days: n.duration_days ?? 1,
          positionAfterId: localAddPos.get(n.draft_id) ?? null,
        };
      });
  }, [pendingNewActivities, localAddPos, paletteByKey]);

  const cancel = React.useCallback(() => {
    setLocalAddPos(new Map());
  }, []);

  return {
    addPos: localAddPos,
    localAdds,
    commit: handleInlineAdd,
    cancel,
    onHoldChange: onHold,
  };
}
